import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Wind, Pencil, Trash2, Send, Copy, Check } from "lucide-react";

const TECH_KEYS = ["box", "478", "coherent", "physiological_sigh", "cooling", "bhramari", "nadi_shodhana", "ocean", "wim_hof", "kapalabhati"];
const SCHED_KEYS = ["all_daily", "rotate"];
const EMPTY = { name: "", description: "", duration_days: 7, schedule: "all_daily", techniques: [] };

export default function BreathProgramsTab() {
  const t = useT();
  const [programs, setPrograms] = useState(null);
  const [clients, setClients] = useState([]);
  const [editor, setEditor] = useState(null); // { program } | { new: true }
  const [assign, setAssign] = useState(null); // program being assigned
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, cl] = await Promise.all([
        api.get("/breath-programs").then((r) => r.data?.programs || []).catch(() => []),
        api.get("/coach/plan-clients").then((r) => r.data).catch(() => []),
      ]);
      setPrograms(Array.isArray(p) ? p : []);
      setClients(Array.isArray(cl) ? cl : []);
    } catch { setPrograms([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeProgram = async (p) => {
    if (!window.confirm(t("bp.archive_confirm", { name: p.name }))) return;
    try { await api.delete(`/breath-programs/${p.id}`); toast.success(t("bp.archived")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const copyCode = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 1500); };

  return (
    <div data-testid="pl-programs-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("bp.sub")}</p>
        <Button className="gap-1.5 rounded-full" onClick={() => setEditor({ new: true })} data-testid="bp-new"><Plus className="h-4 w-4" /> {t("bp.new")}</Button>
      </div>

      {programs === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : programs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Wind className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("bp.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("bp.empty_sub")}</p>
          <Button className="mt-4 gap-1.5 rounded-full" onClick={() => setEditor({ new: true })} data-testid="bp-empty-cta"><Plus className="h-4 w-4" /> {t("bp.new")}</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {programs.map((p) => (
            <div key={p.id} data-testid={`bp-row-${p.id}`} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Wind className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{p.name}</p>
                  {p.description && <p className="truncate text-xs text-muted-foreground">{p.description}</p>}
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {t("bp.techniques_n", { n: (p.techniques || []).length })} · {t("bp.days_n", { n: p.duration_days })} · {t(`bp.sched_${p.schedule}`)}
                    {p.assignments_count ? ` · ${t("bp.assigned_n", { n: p.assignments_count })}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setEditor({ program: p })} data-testid={`bp-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" onClick={() => removeProgram(p)} data-testid={`bp-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  <Button size="sm" className="gap-1.5 rounded-full" onClick={() => setAssign(p)} data-testid={`bp-assign-${p.id}`}><Send className="h-3.5 w-3.5" /> {t("bp.share")}</Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(p.techniques || []).map((tech, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground">
                    {t(`bp.tech_${tech.key}`)} · {tech.duration_min}{t("bp.min")}
                  </span>
                ))}
              </div>
              <button type="button" onClick={() => copyCode(p.share_code)} data-testid={`bp-code-${p.id}`} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1 text-xs font-mono text-muted-foreground hover:bg-accent">
                {copied === p.share_code ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />} {p.share_code}
              </button>
            </div>
          ))}
        </div>
      )}

      {editor && <ProgramEditor program={editor.program || null} onClose={(saved) => { setEditor(null); if (saved) load(); }} />}
      {assign && <AssignDialog program={assign} clients={clients} onClose={() => { setAssign(null); load(); }} />}
    </div>
  );
}

function ProgramEditor({ program, onClose }) {
  const t = useT();
  const isEdit = !!program;
  const [form, setForm] = useState(() => program
    ? { name: program.name || "", description: program.description || "", duration_days: program.duration_days || 7, schedule: program.schedule || "all_daily", techniques: (program.techniques || []).map((x) => ({ ...x })) }
    : { ...EMPTY, techniques: [] });
  const [saving, setSaving] = useState(false);

  const has = (key) => form.techniques.some((x) => x.key === key);
  const toggleTech = (key) => setForm((f) => ({
    ...f,
    techniques: has(key) ? f.techniques.filter((x) => x.key !== key) : [...f.techniques, { key, duration_min: 5, note: "" }],
  }));
  const setTech = (key, patch) => setForm((f) => ({ ...f, techniques: f.techniques.map((x) => (x.key === key ? { ...x, ...patch } : x)) }));

  const valid = form.name.trim() && form.techniques.length >= 3 && form.techniques.length <= 5;

  const save = async () => {
    if (!valid) { toast.error(t("bp.need_valid")); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        duration_days: parseInt(form.duration_days) || 7,
        schedule: form.schedule,
        techniques: form.techniques.map((x) => ({ key: x.key, duration_min: parseInt(x.duration_min) || 5, note: (x.note || "").trim() || null })),
      };
      if (isEdit) await api.patch(`/breath-programs/${program.id}`, body);
      else await api.post("/breath-programs", body);
      toast.success(isEdit ? t("bp.updated") : t("bp.created"));
      onClose(true);
    } catch (err) { toast.error(apiError(err, t("bp.save_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="bp-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("bp.editor_edit_title") : t("bp.editor_new_title")}</DialogTitle>
          <DialogDescription>{t("bp.editor_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("bp.name_ph")} data-testid="bp-name" />
          <Textarea className="min-h-[64px]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("bp.desc_ph")} data-testid="bp-desc" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("bp.days_label")}</label>
              <Input type="number" min="3" max="30" value={form.duration_days} onChange={(e) => setForm((f) => ({ ...f, duration_days: e.target.value }))} data-testid="bp-days" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("bp.rhythm_label")}</label>
              <div className="flex gap-1">
                {SCHED_KEYS.map((k) => (
                  <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, schedule: k }))} data-testid={`bp-sched-${k}`}
                    className={`flex-1 rounded-lg border px-2 py-2 text-xs transition-colors ${form.schedule === k ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    {t(`bp.sched_${k}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("bp.tech_count", { n: form.techniques.length })}</label>
            <div className="flex flex-wrap gap-1.5">
              {TECH_KEYS.map((key) => (
                <button key={key} type="button" onClick={() => toggleTech(key)} data-testid={`bp-tech-${key}`}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${has(key) ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {t(`bp.tech_${key}`)}
                </button>
              ))}
            </div>
            {form.techniques.length > 0 && (
              <div className="mt-3 space-y-2">
                {form.techniques.map((x) => (
                  <div key={x.key} className="flex items-center gap-2 rounded-xl border border-border p-2.5" data-testid={`bp-techrow-${x.key}`}>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{t(`bp.tech_${x.key}`)}</span>
                    <Input type="number" min="1" max="60" value={x.duration_min} onChange={(e) => setTech(x.key, { duration_min: e.target.value })} className="h-8 w-20" data-testid={`bp-dur-${x.key}`} />
                    <span className="text-xs text-muted-foreground">{t("bp.min")}</span>
                    <Input value={x.note || ""} onChange={(e) => setTech(x.key, { note: e.target.value })} placeholder={t("bp.note_ph")} className="h-8 w-28" data-testid={`bp-note-${x.key}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onClose(false)}>{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving || !valid} data-testid="bp-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ program, clients, onClose }) {
  const t = useT();
  const [sel, setSel] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const doAssign = async () => {
    if (!sel) return;
    setSending(true);
    try {
      await api.post(`/breath-programs/${program.id}/assign`, { client_id: sel, note: note.trim() || undefined });
      toast.success(t("bp.assigned_ok"));
      onClose();
    } catch (err) { toast.error(apiError(err, t("bp.assign_failed"))); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="bp-assign-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{t("bp.share_title", { name: program.name })}</DialogTitle>
          <DialogDescription>{t("bp.share_desc", { code: program.share_code })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {clients.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("bp.no_clients")}</p>
          ) : clients.map((c) => (
            <button key={c.id} onClick={() => setSel(c.id)} data-testid={`bp-client-${c.id}`} className={`flex w-full items-center gap-2 rounded-xl border p-2.5 text-left ${sel === c.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${sel === c.id ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>{sel === c.id && <Check className="h-3 w-3" />}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{c.name || c.email}</span>
            </button>
          ))}
        </div>
        <Textarea className="mt-2 min-h-[60px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("bp.assign_note_ph")} data-testid="bp-assign-note" />
        <Button className="mt-1 w-full gap-1.5 rounded-full" disabled={!sel || sending} onClick={doAssign} data-testid="bp-do-assign"><Send className="h-4 w-4" /> {sending ? t("bp.sending") : t("bp.assign")}</Button>
      </DialogContent>
    </Dialog>
  );
}
